import React, { useEffect, useState } from "react";
import {
    Button, Card, CardContent, CardActions, Typography, Grid,
    Container, Dialog, DialogTitle, DialogContent, RadioGroup,
    FormControlLabel, Radio, Box, Chip, makeStyles, CircularProgress,
    ButtonGroup, Paper
} from "@material-ui/core";
import {
    Check, CloudDownload, Language, Archive, GetApp,
    Speed, Star, Warning
} from "@material-ui/icons";
import API from "../../middleware/Api";
import { useDispatch } from "react-redux";
import { toggleSnackbar } from "../../redux/explorer";

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    header: {
        textAlign: "center",
        marginBottom: theme.spacing(4),
    },
    statusCard: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2, 3),
        borderRadius: 12,
    },
    statusExpiring: {
        backgroundColor: "#fff3e0",
        border: "1px solid #ff9800",
    },
    statusDanger: {
        backgroundColor: "#ffebee",
        border: "1px solid #f44336",
    },
    statusNormal: {
        backgroundColor: "#e8f5e9",
        border: "1px solid #4caf50",
    },
    statusWarning: {
        color: "#e65100",
        fontWeight: 600,
    },
    statusDangerText: {
        color: "#c62828",
        fontWeight: 700,
    },
    card: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 16,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: theme.shadows[8],
        },
    },
    cardPopular: {
        border: `2px solid ${theme.palette.primary.main}`,
        position: "relative",
    },
    popularBadge: {
        position: "absolute",
        top: -12,
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        padding: "2px 16px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
    },
    price: {
        fontWeight: 700,
        fontSize: 36,
        color: theme.palette.primary.main,
    },
    priceSmall: {
        fontWeight: 600,
        fontSize: 20,
        color: theme.palette.primary.main,
    },
    priceUnit: {
        fontSize: 14,
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
    feature: {
        display: "flex",
        alignItems: "center",
        marginBottom: 8,
    },
    featureIcon: {
        color: theme.palette.success.main,
        marginRight: 8,
        fontSize: 18,
    },
    featureDisabled: {
        color: theme.palette.text.disabled,
        marginRight: 8,
        fontSize: 18,
    },
    dialogContent: {
        minWidth: 300,
    },
    savings: {
        color: theme.palette.success.main,
        fontWeight: 600,
        fontSize: 13,
    },
    buyButtons: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "100%",
    },
    monthlyBtn: {},
    yearlyBtn: {
        position: "relative",
    },
    saveChip: {
        position: "absolute",
        top: -8,
        right: -8,
        fontSize: 10,
        height: 20,
    },
}));

const formatStorage = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024 * 1024)).toFixed(1) + " TB";
    if (bytes >= 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(0) + " GB";
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(0) + " MB";
    return bytes + " B";
};

const parseFeatures = (featuresStr) => {
    try { return JSON.parse(featuresStr); }
    catch { return []; }
};

export default function MembershipPage() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [membership, setMembership] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [selectedIsYearly, setSelectedIsYearly] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("epay_usdt");
    const [submitting, setSubmitting] = useState(false);

    const loadLevels = () => {
        API.get("/membership/levels")
            .then((response) => setLevels(response.data))
            .catch((error) => dispatch(toggleSnackbar("top", "right", error.message, "error")))
            .finally(() => setLoading(false));
    };

    const loadMembership = () => {
        API.get("/user/membership")
            .then((response) => {
                if (response.data && response.data.has_membership) {
                    setMembership(response.data);
                }
            })
            .catch(() => { /* 未登录或无会员时静默 */ });
    };

    const checkStatus = () => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("status");
        if (status === "success") {
            dispatch(toggleSnackbar("top", "right", "支付成功！会员已激活", "success"));
            loadMembership(); // 刷新会员状态
            window.history.replaceState({}, "", "/membership");
        } else if (status === "failed") {
            dispatch(toggleSnackbar("top", "right", "支付失败，请重试", "error"));
            window.history.replaceState({}, "", "/membership");
        }
    };

    useEffect(() => {
        loadLevels();
        loadMembership();
        checkStatus();
    }, []);

    const handleBuy = (level, isYearly) => {
        setSelectedLevel(level);
        setSelectedIsYearly(isYearly);
        setDialogOpen(true);
    };

    const handleSubmitPayment = () => {
        setSubmitting(true);
        API.post("/payment/order", {
            level_id: selectedLevel.ID,
            payment_method: paymentMethod,
            is_yearly: selectedIsYearly,
        })
            .then((response) => {
                const { pay_url, params } = response.data;
                const form = document.createElement("form");
                form.method = "POST";
                form.action = pay_url;
                form.style.display = "none";
                Object.keys(params).forEach((key) => {
                    const input = document.createElement("input");
                    input.name = key;
                    input.value = params[key];
                    form.appendChild(input);
                });
                document.body.appendChild(form);
                form.submit();
            })
            .catch((error) => {
                dispatch(toggleSnackbar("top", "right", error.message, "error"));
                setSubmitting(false);
            });
    };

    const priceToShow = selectedLevel
        ? (selectedIsYearly && selectedLevel.YearlyPrice > 0
            ? selectedLevel.YearlyPrice
            : selectedLevel.PriceUSD)
        : 0;

    if (loading) {
        return (
            <Container className={classes.root} maxWidth="lg">
                <Box display="flex" justifyContent="center" mt={8}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    // 会员状态卡片样式
    let statusClass = classes.statusNormal;
    let warningLabel = null;
    if (membership) {
        const days = membership.days_remaining || 0;
        if (days <= 7) {
            statusClass = classes.statusDanger;
            warningLabel = (
                <Typography className={classes.statusDangerText}>
                    <Warning fontSize="small" style={{ verticalAlign: "middle", marginRight: 4 }} />
                    还剩 {days} 天！请立即续费，到期后文件将被删除！
                </Typography>
            );
        } else if (days <= 30) {
            statusClass = classes.statusExpiring;
            warningLabel = (
                <Typography className={classes.statusWarning}>
                    <Warning fontSize="small" style={{ verticalAlign: "middle", marginRight: 4 }} />
                    还剩 {days} 天，建议在到期前续费，避免文件被删除
                </Typography>
            );
        }
    }

    return (
        <Container className={classes.root} maxWidth="lg">
            {/* 会员状态卡片 */}
            {membership && (
                <Paper className={`${classes.statusCard} ${statusClass}`}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        <Box>
                            <Typography variant="h6">
                                当前会员：
                                <strong>{membership.level || "Pro"}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                到期时间：{new Date((membership.expire_at || 0) * 1000).toLocaleDateString("zh-CN")}
                            </Typography>
                        </Box>
                        <Chip
                            label={`剩余 ${membership.days_remaining || 0} 天`}
                            style={{
                                backgroundColor: (membership.days_remaining || 0) <= 30 ? "#f44336" : "#4caf50",
                                color: "#fff",
                                fontWeight: 700,
                            }}
                        />
                    </Box>
                    {warningLabel && <Box mt={1}>{warningLabel}</Box>}
                </Paper>
            )}

            <div className={classes.header}>
                <Typography variant="h4" gutterBottom>
                    选择适合您的会员方案
                </Typography>
                <Typography color="textSecondary">
                    升级会员，解锁更多存储空间和高级功能
                </Typography>
            </div>

            <Grid container spacing={3} justifyContent="center">
                {levels.map((level) => {
                    const hasYearly = level.YearlyPrice > 0;
                    const isFree = level.PriceUSD === 0;
                    const yearlySavings = hasYearly ? Math.round(level.PriceUSD * 12 - level.YearlyPrice) : 0;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={level.ID}>
                            <Card className={`${classes.card} ${level.SortOrder === 1 ? classes.cardPopular : ""}`}>
                                {level.SortOrder === 1 && (
                                    <div className={classes.popularBadge}>推荐</div>
                                )}
                                <CardContent style={{ flexGrow: 1 }}>
                                    <Typography variant="h5" gutterBottom>{level.Name}</Typography>
                                    <Typography color="textSecondary" gutterBottom>
                                        {formatStorage(level.StorageBytes)}
                                    </Typography>

                                    <Box mt={2} mb={1}>
                                        {isFree ? (
                                            <Box>
                                                <span className={classes.price}>免费</span>
                                                <span className={classes.priceUnit}> 永久</span>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Box display="flex" alignItems="baseline" mb={0.5}>
                                                    <span className={classes.priceSmall}>${level.PriceUSD}</span>
                                                    <span className={classes.priceUnit}>/月</span>
                                                </Box>
                                                {hasYearly && (
                                                    <Box display="flex" alignItems="baseline">
                                                        <span className={classes.priceSmall}>${level.YearlyPrice}</span>
                                                        <span className={classes.priceUnit}>/年</span>
                                                        <Chip
                                                            size="small"
                                                            label={`省 $${yearlySavings}`}
                                                            className={classes.savings}
                                                            style={{ marginLeft: 8, backgroundColor: "#e8f5e9", color: "#2e7d32", height: 20, fontSize: 11 }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box mt={2}>
                                        {parseFeatures(level.Features).filter(f => f.Enabled).map((f, i) => (
                                            <div className={classes.feature} key={i}>
                                                <span className={classes.featureIcon}>
                                                    <Check />
                                                </span>
                                                <Typography variant="body2">{f.Name}</Typography>
                                            </div>
                                        ))}
                                    </Box>
                                </CardContent>

                                <CardActions>
                                    {isFree ? (
                                        <Button fullWidth variant="outlined" disabled>
                                            当前方案
                                        </Button>
                                    ) : (
                                        <div className={classes.buyButtons}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color={level.SortOrder === 1 ? "primary" : "default"}
                                                onClick={() => handleBuy(level, false)}
                                            >
                                                月付 ${level.PriceUSD}
                                            </Button>
                                            {hasYearly && (
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color={level.SortOrder === 1 ? "primary" : "default"}
                                                    onClick={() => handleBuy(level, true)}
                                                >
                                                    年付 ${level.YearlyPrice} (省 ${yearlySavings})
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>
                    确认支付 — {selectedLevel?.Name} {selectedIsYearly ? "年付" : "月付"}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography variant="subtitle1" gutterBottom>
                        应付金额：<strong>${priceToShow} USD</strong>
                    </Typography>

                    <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                        <FormControlLabel
                            value="epay_usdt"
                            control={<Radio />}
                            label={
                                <Box display="flex" alignItems="center">
                                    <Chip size="small" label="USDT" style={{ marginRight: 8 }} />
                                    USDT多链支付
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="epay_wx"
                            control={<Radio />}
                            label={
                                <Box display="flex" alignItems="center">
                                    <Chip size="small" label="微信" color="primary" style={{ marginRight: 8 }} />
                                    微信支付
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="epay_alipay"
                            control={<Radio />}
                            label={
                                <Box display="flex" alignItems="center">
                                    <Chip size="small" label="支付宝" style={{ marginRight: 8, backgroundColor: "#1677FF", color: "#fff" }} />
                                    支付宝支付
                                </Box>
                            }
                        />
                    </RadioGroup>

                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Button onClick={() => setDialogOpen(false)} style={{ marginRight: 8 }}>
                            取消
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmitPayment}
                            disabled={submitting}
                        >
                            {submitting ? <CircularProgress size={20} /> : "确认支付"}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Container>
    );
}
